/**
 * IDE 风格 Prompt 编辑器
 * 深色/浅色切换、行号、{变量}自动补全
 */
import React, { useState, useRef, useCallback } from 'react';
import { Button } from 'antd';
import { BulbOutlined, SunOutlined, MoonOutlined } from '@ant-design/icons';
import './PromptEditor.css';

const PromptEditor = ({ value, onChange, availableColumns = [] }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompletePos, setAutocompletePos] = useState({ top: 0, left: 0 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [filterText, setFilterText] = useState('');
  const textareaRef = useRef(null);
  const wrapperRef = useRef(null);

  const lineCount = (value || '').split('\n').length;

  const filteredColumns = availableColumns.filter((col) =>
    col.toLowerCase().includes(filterText.toLowerCase())
  );

  const insertVariable = useCallback((colName) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const pos = textarea.selectionStart;
    const text = value || '';
    // Find the opening { before cursor
    const beforeCursor = text.slice(0, pos);
    const braceIdx = beforeCursor.lastIndexOf('{');
    const newText =
      text.slice(0, braceIdx) + '{' + colName + '}' + text.slice(pos);
    onChange(newText);
    setShowAutocomplete(false);
    // Set cursor after inserted variable
    setTimeout(() => {
      const newPos = braceIdx + colName.length + 2;
      textarea.selectionStart = newPos;
      textarea.selectionEnd = newPos;
      textarea.focus();
    }, 0);
  }, [value, onChange]);

  const handleKeyDown = (e) => {
    if (showAutocomplete && filteredColumns.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev < filteredColumns.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev > 0 ? prev - 1 : filteredColumns.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertVariable(filteredColumns[activeIndex]);
      } else if (e.key === 'Escape') {
        setShowAutocomplete(false);
      }
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Check if we should show autocomplete
    const pos = e.target.selectionStart;
    const textBefore = newValue.slice(0, pos);
    const lastBrace = textBefore.lastIndexOf('{');
    const lastClose = textBefore.lastIndexOf('}');

    if (lastBrace > lastClose) {
      const filter = textBefore.slice(lastBrace + 1);
      setFilterText(filter);
      setActiveIndex(0);
      setShowAutocomplete(true);
      // Position autocomplete near cursor
      const textarea = textareaRef.current;
      if (textarea && wrapperRef.current) {
        const rect = textarea.getBoundingClientRect();
        // Approximate position
        const lines = textBefore.split('\n');
        const lineNum = lines.length;
        const top = lineNum * 22.4 + 40; // line-height * lines + toolbar
        setAutocompletePos({
          top: Math.min(top, rect.height + 40),
          left: 60,
        });
      }
    } else {
      setShowAutocomplete(false);
    }
  };

  return (
    <div className="prompt-editor-wrapper" ref={wrapperRef}>
      <div className={`prompt-editor-container ${darkMode ? '' : 'light'}`}>
        {/* Toolbar */}
        <div className="prompt-editor-toolbar">
          <div className="prompt-editor-toolbar-left">
            <BulbOutlined />
            <span>Prompt 编辑器</span>
          </div>
          <div className="prompt-editor-toolbar-right">
            <Button
              type="text"
              size="small"
              icon={darkMode ? <SunOutlined /> : <MoonOutlined />}
              onClick={() => setDarkMode(!darkMode)}
              style={{ color: darkMode ? '#a6adc8' : 'var(--color-text-secondary)' }}
            />
          </div>
        </div>

        {/* Editor body */}
        <div className="prompt-editor-body">
          {/* Line numbers */}
          <div className="prompt-editor-lines">
            {Array.from({ length: Math.max(lineCount, 8) }, (_, i) => (
              <div key={i} className="prompt-editor-line-number">
                {i + 1}
              </div>
            ))}
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            className="prompt-editor-textarea"
            value={value || ''}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={`输入打标指令...\n\n示例：\n- 根据文本内容判断情感：正面/负面/中性\n- 分析图片中的产品类型\n- 输入 { 插入列变量`}
            spellCheck={false}
          />
        </div>

        {/* Autocomplete dropdown */}
        {showAutocomplete && filteredColumns.length > 0 && (
          <div
            className="prompt-autocomplete"
            style={{
              top: autocompletePos.top,
              left: autocompletePos.left,
            }}
          >
            {filteredColumns.map((col, idx) => (
              <div
                key={col}
                className={`prompt-autocomplete-item ${idx === activeIndex ? 'active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertVariable(col);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
              >
                {'{' + col + '}'}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptEditor;